import { registerTenant, TenantId } from '~/tenants/common/registry';
import { TimeZone } from '~/lib/enum';
import { TwtOrderSchema } from '~/tenants/twt/model/TwtOrder';
import { twtTenantOptions } from '~/tenants/twt/register';

/**
 * bmd — Bleenk Media, a CAMPAIGNS-ONLY tenant.
 *
 * This tenant exists so Bleenk's cold outreach (Tampa agent demo pages) runs
 * through the SAME campaign machinery Twist uses — the batch approval queue,
 * the suppression list, the warmup ramp, the deliverability auto-pauses —
 * rather than through anything hand-rolled. Founder-directed Aug 9.
 *
 * IT DOES NOT TAKE ORDERS. Like twt2, it reuses twt's order/customer/provider
 * classes, forms, performables and packages wholesale via twtTenantOptions(),
 * purely because TenantConfig requires them — they are STUBS here and none of
 * them is reachable in anger, because:
 *   - domain is 'bmd.invalid' (can never resolve; the tenant is reached via
 *     the ?tenant=bmd query param that tenant_optional() supports),
 *   - payments/inbox/nylas identity fields are overridden off below, and it
 *     has no Resend key (see below).
 *
 * Stubbed-from-twt fields (order stack): orderClass, customerClass,
 * providerClass, forms, performables, packages, actions, providers, brands
 * (stripped), locations (replaced with Tampa), schedule/autorespond settings
 * (inherited, inert without providers).
 *
 * WHAT ACTUALLY MATTERS HERE:
 *   - identity: name/legal/email — used in campaign sign-offs and CAN-SPAM.
 *   - locations[0].address — the CAN-SPAM postal-address fallback and the
 *     recipient-timezone fallback (US Eastern for Tampa).
 *   - Resend key: DELIBERATELY ABSENT until Bleenk Media has its own Resend
 *     account with bleenkmedia.com verified. campaignSendEmail skips with
 *     'no_key' until then, so nothing can go out early by accident. Keys are
 *     not in this file (it ships to the browser): bmd is left out of
 *     PLATFORM_RESEND_TENANTS in tenants/common/credentials.server.ts, so it
 *     has no key until RESEND_API_KEY_BMD is set on the workers. DO NOT add
 *     it to that list or point it at Twist's key.
 *   - emailEnable: true, so the moment the key + verified outreach domain
 *     land, sending lights up exactly as Twist's did.
 *
 * Campaign runtime config (outreach_domain 'bleenkmedia.com', sender, daily
 * budget 5, link_domain 'bleenk.com') lives in the tenant DB row
 * (data.company.campaign, TenantCampaignSchema) — seeded by
 * scripts/tampa-contacts-import.ts, not here.
 *
 * ONE-REMINDER RULE: the approved copy promises "I won't follow up more than
 * once". The library rotation sends each enabled campaign once per contact,
 * so the cap is structural: this tenant's campaign library must never hold
 * more than TWO enabled campaigns for the same segment (the opener plus at
 * most one follow-up). The import script seeds exactly one.
 *
 * D1: production needs a DB_bmd wrangler binding + migrations (create-tenant
 * CLI provisions it, same as twt2). Locally/staging the shared env.DB is used
 * by db_client(), so nothing needs provisioning to develop against it.
 *
 * Cron: worker/inngest/src/schema.ts INNGEST_TENANTS currently runs only twt
 * in production — add TenantId.BLEENK_MEDIA there at go-live so campaign_cron
 * ticks this tenant. Deliberately not added now: everything stays behind the
 * approval queue and the tenant should not tick before the domain exists.
 */
export default function registerBmd() {
  registerTenant(TenantId.BLEENK_MEDIA, TwtOrderSchema, {
    ...twtTenantOptions(),
    name: 'Bleenk Media',
    legal: 'Bleenk Media',
    domain: 'bmd.invalid',
    email: 'paul@bleenk.com',
    emailTech: 'paul@bleenk.com',
    emailFrom: undefined,
    phone: '+18135550000',
    background: '#111111',
    logoRaster: 'https://bleenk.com/logo.png',
    logoVector: undefined,
    brands: undefined,
    // Order-stack integrations off: this tenant never invoices or schedules.
    rainforestEnable: false,
    rainforestMerchantId: undefined,
    rainforestMerchantApplicationId: undefined,
    inboxEnabled: false,
    nylasProviderInvite: undefined,
    userback: false,
    // No Resend key until Bleenk Media's own account exists — see header.
    emailEnable: true,
    locations: [
      {
        slug: 'tpa',
        name: 'Tampa',
        timezone: TimeZone.US_EASTERN,
        address: {
          line1: 'Tampa',
          city: 'Tampa',
          state: 'FL',
          zip: '33602',
          long: -82.4572,
          lat: 27.9506,
        },
      },
    ],
  });
}
